import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { PlacementType, } from '@amzn/handshake-painter-typescript-client/v1';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { useDefaultApiParams } from './useDefaultApiParams';
import { STOREFRONTS, STOREFRONT_STRATEGIES } from 'src/pages/storefrontPage/utils';
import { useAnalyticsContext } from 'src/common/metrics';
import storefrontMetadata from 'src/pages/storefrontPage/storefrontMetadata.json';
/**
 * Resolves storefront metadata and fetches products.
 * - REGULAR storefronts → product widget API (ASIN-based)
 * - DEALS storefronts   → storefront API (strategy + category)
 */
export const useStorefront = (options) => {
    var _a, _b, _c, _d;
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { getDefaultParams } = useDefaultApiParams();
    const rumClient = useAnalyticsContext();
    const isEnabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
    const isValidStorefront = !!id && id in STOREFRONTS;
    const storefrontId = isValidStorefront ? id : 'defaultStorefront';
    const storeMeta = storefrontMetadata[STOREFRONTS[storefrontId]];
    const isDeals = storeMeta.type === 'DEALS';
    const asins = 'products' in storeMeta ? storeMeta.products : [];
    const strategyConfig = STOREFRONT_STRATEGIES[storefrontId];
    const validCategories = new Set((_b = strategyConfig === null || strategyConfig === void 0 ? void 0 : strategyConfig.validCategories) !== null && _b !== void 0 ? _b : []);
    const rawCategories = (_d = (_c = searchParams.get('storefrontCategories')) === null || _c === void 0 ? void 0 : _c.split(',').filter(Boolean)) !== null && _d !== void 0 ? _d : [];
    const invalidCategories = [];
    const storefrontCategories = rawCategories.filter((cat) => validCategories.has(cat) || (invalidCategories.push(cat), false));
    const hasInvalidCategories = invalidCategories.length > 0;
    const hasNoCategories = isDeals && storefrontCategories.length === 0;
    // Analytics logging
    useEffect(() => {
        if (id && !isValidStorefront) {
            rumClient.recordEvent('UNKNOWN_STOREFRONT_ID', { requestedId: id, url: window.location.href });
        }
        if (hasInvalidCategories) {
            rumClient.recordEvent('INVALID_STOREFRONT_CATEGORIES', {
                storefrontId,
                invalidCategories: invalidCategories.join(','),
                url: window.location.href,
            });
        }
        if (hasNoCategories) {
            rumClient.recordEvent('EMPTY_STOREFRONT_CATEGORIES', { storefrontId, url: window.location.href });
        }
    }, [id, isValidStorefront, storefrontId, hasInvalidCategories, hasNoCategories, rumClient]);
    const strategy = strategyConfig === null || strategyConfig === void 0 ? void 0 : strategyConfig.strategy;
    const firstCategory = storefrontCategories[0];
    const canFetch = isDeals ? !!strategy && !!firstCategory : asins.length > 0;
    const result = useQuery({
        queryKey: isDeals ? ['getStorefrontProducts', strategy, firstCategory] : ['getProductWidget', asins],
        queryFn: () => {
            const params = getDefaultParams();
            return isDeals
                ? HandshakePainterServiceApiV1.getStorefrontProducts(Object.assign({ strategy, category: firstCategory }, params))
                : HandshakePainterServiceApiV1.getProductWidget(Object.assign({ asins, placement: PlacementType.CustomersAlsoBought }, params));
        },
        enabled: isEnabled && canFetch,
        staleTime: Infinity,
    });
    return Object.assign(Object.assign({}, result), { storefrontId, storeMeta, storefrontCategories });
};
/**
 * Fetches products for a specific category tab via the generated client.
 * Resolves strategy from storefrontId internally.
 */
export const useCategoryProducts = (storefrontId, category) => {
    var _a;
    const { getDefaultParams } = useDefaultApiParams();
    const strategy = storefrontId
        ? (_a = STOREFRONT_STRATEGIES[storefrontId]) === null || _a === void 0 ? void 0 : _a.strategy
        : undefined;
    return useQuery({
        queryKey: ['getStorefrontProducts', strategy, category],
        queryFn: () => HandshakePainterServiceApiV1.getStorefrontProducts(Object.assign({ strategy: strategy, category }, getDefaultParams())),
        enabled: !!strategy && !!category,
        staleTime: Infinity,
    });
};
